import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  ChevronDown,
  ChevronUp,
  Shuffle,
  Check,
  Info,
  Loader2,
  Lock,
  MapPin,
  Package,
  Search,
  Undo2,
  X,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { useDebounce } from '@/hooks/useDebounce';
import { Segmentado } from '@/componentes/ui/segmentado';
import { TransferenciaCentros } from '@/componentes/transferencia/TransferenciaCentros';
import { TransferenciaLocalizadores } from '@/componentes/transferencia/TransferenciaLocalizadores';
import { useCorredores, useProduto } from '@/hooks/consultas';
import { cn, compararNatural, numero } from '@/lib/utils';
import type { ProdutoEncontrado, VagaMapa } from '@/lib/tipos';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Campo, Pilula } from '@/componentes/ui/primitivos';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';
import {
  comoAlcancar,
  comoEndereco,
  DICA_ALCANCE,
  lerEndereco,
  locDoId,
  RackRua,
  useVagaSobCursor,
  type ModoArraste,
} from '@/componentes/movimentacao/rack';

/**
 * Transferências — mover estoque entre posições, sem pretexto.
 *
 * A tela de negativos resolve um caso específico: o destino já está escolhido
 * (o buraco) e falta a saída. Aqui não há caso: o operador escolhe as duas
 * pontas e a quantidade.
 *
 * Ela aceita as três entradas que a operação usa de verdade, porque o que se
 * sabe primeiro varia:
 *
 *   - **pelo produto** — "preciso mover este SKU"; a tela mostra onde ele está;
 *   - **pela descrição** — quando só se lembra do nome;
 *   - **pela posição** — "vou esvaziar esta vaga"; a tela mostra o que há nela.
 *
 * E aceita as duas formas de escolher o destino, porque também variam: quem
 * já sabe a vaga exata arrasta direto até ela; quem só sabe a RUA troca de rua
 * no seletor (ou arrastando até a borda do desenho), olha a ocupação e só
 * então decide o módulo e o box. É a diferença entre conhecer o endereço e
 * precisar ver a rua para escolher.
 *
 * O desenho, o arraste, a paginação por arraste e o realce sob o cursor vêm de
 * `componentes/movimentacao/rack` — os mesmos da tela de negativos.
 *
 * O painel do topo encolhe num clique (`CHAVE_PAINEL`, lembrado entre
 * sessões): com a saída já escolhida, o que importa é o desenho, e cada linha
 * de cabeçalho é uma linha a menos de rua visível. Encolhido, ele vira uma
 * faixa só, que ainda diz o essencial — SKU, de onde sai, para onde vai.
 *
 * O cabeçalho é deliberadamente espremido em duas faixas. Tudo ali é dado
 * relevante — produto, EAN, peso, as duas pontas, as posições que podem ceder
 * saldo — mas nenhum deles é a DECISÃO: a decisão é para onde mandar, e ela se
 * toma olhando o desenho. Então o desenho fica com toda a altura que sobra, e
 * o resto vira uma linha só, com rolagem lateral onde precisar.
 *
 * Regra do PBS: as duas pernas no MESMO centro; a procedure recebe um único
 * `@CENTRO_ESTOQUE`. E a saída exige saldo — `Usp_ValidaTransferenciaLocalizador`
 * recusa saída de posição sem saldo do SKU.
 */

interface PosicaoDoSku {
  localizador: number;
  descricao: string;
  corredor: string;
  saldo: number;
}

interface ProdutoNaPosicao {
  sku: number;
  descricao: string;
  quantidade: number;
}

interface PosicaoResolvida {
  localizador: number;
  endereco: string;
  corredor: string;
  produtos: ProdutoNaPosicao[];
}

type RespostaBusca =
  | {
      tipoDetectado: 'sku';
      termo: string;
      posicoes: PosicaoDoSku[];
      total: number;
      /** Saldo do SKU no centro como o PBS mostra (negativos e sem endereço dentro). */
      saldoErp?: number;
    }
  | {
      tipoDetectado: 'endereco';
      termo: string;
      enderecos: { localizador: number; descricao: string }[];
      total: number;
    }
  | {
      tipoDetectado: 'descricao';
      termo: string;
      produtos: { produto: number; descricao: string }[];
      total: number;
    };

/**
 * O resumo do SKU no topo: primeiro o saldo do PBS, que é o número que a
 * operação confere no ERP; depois o que está em posição para pegar. A diferença
 * (negativo e saldo sem endereço) aparece com nome — sem isso o 678 do WMS
 * contra o 668 do PBS parecia erro de um dos dois (3201, SKU 12713).
 */
function resumoDoSku(b: { posicoes: PosicaoDoSku[]; saldoErp?: number }): string {
  const r = numerosDoSku(b);
  return `${numero(r.total)} un em ${numero(r.posicoes)} ${r.posicoes === 1 ? 'posição' : 'posições'}`;
}

/** Os números do resumo, para a faixa montar com o selo do "a corrigir". */
interface NumerosDoSku {
  total: number;
  posicoes: number;
  /** Disponível em posição − saldo do PBS. Negativo = há o que corrigir. */
  aCorrigir: number;
  /** Endereços com saldo negativo deste SKU, do mais negativo para o menos. */
  negativos: PosicaoDoSku[];
  /** O que sobra do "a corrigir" depois dos negativos: saldo sem endereço. */
  semEndereco: number;
}

function numerosDoSku(b: { posicoes: PosicaoDoSku[]; saldoErp?: number }): NumerosDoSku {
  const comSaldo = b.posicoes.filter((p) => p.saldo > 0);
  const disponivel = comSaldo.reduce((a, p) => a + p.saldo, 0);
  const total = b.saldoErp ?? disponivel;
  const negativos = b.posicoes.filter((p) => p.saldo < 0).sort((x, y) => x.saldo - y.saldo);
  const somaNegativos = negativos.reduce((a, p) => a + p.saldo, 0);
  return {
    total,
    posicoes: comSaldo.length,
    aCorrigir: total - disponivel,
    negativos,
    semEndereco: total - disponivel - somaNegativos,
  };
}

interface Simulacao {
  ok: boolean;
  impedimentos: string[];
  avisos: string[];
  saldoOrigem: number;
  saldoDestinoAntes: number;
  saldoDestinoDepois: number;
  maximoPorEspaco: number | null;
  pctDestinoDepois: number | null;
}

const CHAVE_PAINEL = 'wms.transferencias.painel';

interface Ponta {
  localizador: number;
  endereco: string;
  corredor: string;
  /** Saldo do SKO na ponta. Na origem é o disponível; no destino, o atual. */
  saldoSku: number | null;
  pct: number | null;
  saldoPosicao: number | null;
}

function TransferenciasEntrePosicoes({ seletor }: { seletor: React.ReactNode }): JSX.Element {
  const { centro } = useContexto();

  const [produto, definirProduto] = React.useState<number | null>(null);
  const [descricaoProduto, definirDescricaoProduto] = React.useState('');

  /**
   * `?sku=` — a tela pode nascer já com o produto carregado.
   *
   * É o que liga a análise de consolidação a esta tela. Lá a lista de "SKUs
   * espalhados" clicava para o ESTOQUE, que só mostra o problema de novo: o
   * relatório terminava em outro relatório. Agora cada linha termina aqui, com
   * o SKU pronto para mover — que é a ação que o alerta pede desde sempre.
   *
   * Só na primeira carga: depois disso quem manda é a busca da própria tela, e
   * reagir à URL faria o produto voltar sozinho ao trocar de item.
   */
  const [skuUrl] = useParamUrl('sku');
  const jaCarregouDaUrl = React.useRef(false);
  React.useEffect(() => {
    if (jaCarregouDaUrl.current) return;
    const n = Number(skuUrl);
    if (!Number.isInteger(n) || n <= 0) return;
    jaCarregouDaUrl.current = true;
    definirProduto(n);
    definirDescricaoProduto('');
  }, [skuUrl]);
  const [origem, definirOrigem] = React.useState<Ponta | null>(null);
  const [destino, definirDestino] = React.useState<Ponta | null>(null);
  const [ruaVista, definirRuaVista] = React.useState<string | null>(null);
  const [arrastando, definirArrastando] = React.useState<string | null>(null);
  const [confirmando, definirConfirmando] = React.useState(false);
  const [painelAberto, definirPainelAberto] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(CHAVE_PAINEL) !== '0';
    } catch {
      return true;
    }
  });

  const alternarPainel = (): void => {
    definirPainelAberto((v) => {
      try {
        localStorage.setItem(CHAVE_PAINEL, v ? '0' : '1');
      } catch {
        /* sessão anônima: só não lembra */
      }
      return !v;
    });
  };

  const { sobreLoc, sobreRef } = useVagaSobCursor(arrastando !== null);
  /*
    AS VAGAS DA RUA PRECISAM RE-RENDERIZAR, e por isso viraram estado.

    Eram um `ref`: bastava para o arraste, que lê no momento do gesto. A
    sugestão de destino não — ela é calculada a partir delas e precisa aparecer
    sozinha quando a rua muda. O `ref` continua ao lado porque os handlers de
    arraste leem durante o gesto, quando um estado ainda não teria chegado.
  */
  const vagasNaTela = React.useRef(new Map<number, VagaMapa>());
  const [vagasDaRua, definirVagasDaRua] = React.useState<Map<number, VagaMapa>>(new Map());
  const registrarVagas = React.useCallback((m: Map<number, VagaMapa>) => {
    vagasNaTela.current = m;
    definirVagasDaRua(m);
  }, []);

  /** A vaga da saída, inteira — dela vêm o andar e o módulo da sugestão. */
  const [vagaDaOrigem, definirVagaDaOrigem] = React.useState<VagaMapa | null>(null);

  /** Onde este SKU tem saldo — as posições que podem ser a saída. */
  const posicoes = useQuery<RespostaBusca>({
    queryKey: ['transferencias', 'posicoes-sku', centro, produto],
    queryFn: () => api.get<RespostaBusca>('/api/busca', { centro, q: String(produto) }),
    enabled: centro !== null && produto !== null,
    staleTime: 30_000,
  });

  const comSaldo: PosicaoDoSku[] = React.useMemo(() => {
    const d = posicoes.data;
    if (!d || d.tipoDetectado !== 'sku') return [];
    return d.posicoes.filter((p) => p.saldo > 0);
  }, [posicoes.data]);

  const resumoSku = React.useMemo(() => {
    const d = posicoes.data;
    return d && d.tipoDetectado === 'sku' ? numerosDoSku(d) : null;
  }, [posicoes.data]);

  const saldoPorVaga = React.useMemo(
    () => new Map(comSaldo.map((p) => [p.localizador, p.saldo])),
    [comSaldo],
  );

  const corredores = useCorredores(centro);
  const ruas = React.useMemo(() => {
    const contagem = new Map<string, number>();
    for (const p of comSaldo) {
      if (p.corredor) contagem.set(p.corredor, (contagem.get(p.corredor) ?? 0) + 1);
    }
    const nomes = [
      ...new Set([...(corredores.data?.corredores ?? []).map((c) => c.corredor), ...contagem.keys()]),
    ].sort(compararNatural);
    return nomes.map((nome) => ({ nome, comSku: contagem.get(nome) ?? 0 }));
  }, [comSaldo, corredores.data]);

  /**
   * As ruas que TÊM o SKU — a navegação do mapa no passo da saída.
   *
   * Aqui a rua não escolhe a origem: ela escolhe QUAL DESENHO está na tela. A
   * origem continua sendo uma posição, e quem a escolhe é o clique na vaga.
   * É a mesma divisão do passo do destino (faixa navega, mapa decide), e foi
   * a falta dela que embaralhou a tela quando a rua virou um passo.
   */
  const ruasComSku = React.useMemo(() => {
    const porRua = new Map<string, { posicoes: number; saldo: number }>();
    for (const p of comSaldo) {
      if (!p.corredor) continue;
      const atual = porRua.get(p.corredor) ?? { posicoes: 0, saldo: 0 };
      atual.posicoes += 1;
      atual.saldo += p.saldo;
      porRua.set(p.corredor, atual);
    }
    return [...porRua.entries()]
      .map(([nome, v]) => ({ nome, ...v }))
      .sort((a, b) => compararNatural(a.nome, b.nome));
  }, [comSaldo]);

  /**
   * ABRE NA RUA COM MAIS SALDO, e não na primeira.
   *
   * O auto-open antigo pegava a primeira da lista e caía em coisas como
   * DIVERGLISA com 1 posição, enquanto as outras 29 ruas ficavam escondidas.
   * A rua com mais peça é a que resolve a maioria das transferências de uma
   * vez — e, como a faixa mostra todas com o saldo escrito, trocar é um
   * clique.
   */
  React.useEffect(() => {
    if (ruaVista !== null || ruasComSku.length === 0) return;
    const maior = ruasComSku.reduce((m, r) => (r.saldo > m.saldo ? r : m), ruasComSku[0]!);
    definirRuaVista(maior.nome);
  }, [ruasComSku, ruaVista]);

  /**
   * As ruas do CD para o DESTINO, com o que interessa: vagas livres.
   *
   * A faixa antiga listava as ruas que têm o SKU — informação de ORIGEM, e a
   * origem já se escolhe no passo anterior. Para largar a carga a pergunta é
   * outra: onde cabe. Por isso aqui entram TODAS as ruas do centro, e o número
   * que cada uma mostra é quantas vagas estão livres.
   */
  const ruasDestino = React.useMemo(
    () =>
      (corredores.data?.corredores ?? [])
        .map((c) => ({
          nome: c.corredor,
          livres: Math.max(0, c.posicoes - c.ocupadas),
          percentual: c.percentual,
        }))
        .sort((a, b) => compararNatural(a.nome, b.nome)),
    [corredores.data],
  );

  /*
    A PAGINAÇÃO ANDA NO CONJUNTO DO PASSO, e não no CD inteiro.

    Arrastar até a borda para trocar de rua é o gesto mais usado do desenho.
    Escolhendo a SAÍDA, passar por 50 ruas sem o SKU para achar a próxima com
    saldo não é navegar, é procurar — então ali ele pula direto para a próxima
    rua que tem o item. Escolhendo o DESTINO, qualquer vaga serve, e o conjunto
    volta a ser o centro todo.
  */
  const ruasNavegaveis = React.useMemo(
    () => (origem === null ? ruasComSku.map((r) => r.nome) : ruas.map((r) => r.nome)),
    [origem, ruasComSku, ruas],
  );
  const indiceRua = ruaVista === null ? -1 : ruasNavegaveis.indexOf(ruaVista);
  const ruaAnterior = indiceRua > 0 ? (ruasNavegaveis[indiceRua - 1] ?? null) : null;
  const ruaSeguinte =
    indiceRua >= 0 && indiceRua < ruasNavegaveis.length - 1
      ? (ruasNavegaveis[indiceRua + 1] ?? null)
      : null;
  const paginarRua = React.useCallback(
    (direcao: -1 | 1): void => {
      const alvo = direcao > 0 ? ruaSeguinte : ruaAnterior;
      if (alvo) definirRuaVista(alvo);
    },
    [ruaAnterior, ruaSeguinte],
  );

  /*
    O DESENHO NÃO ABRE SOZINHO.

    Aqui havia um efeito que escolhia a primeira rua com saldo assim que o SKU
    era buscado. Com uma rua no ar por padrão, a tela respondia uma pergunta
    que ninguém tinha feito — e a escolhida era só a primeira da lista, não a
    que interessava: no SKU 33181 abria DIVERGLISA, 1 posição, enquanto as 29
    outras ruas ficavam atrás de um clique.

    Agora a faixa de ruas é o primeiro passo e o rack só desenha depois da
    escolha. Quem sabe a rua clica nela; quem não sabe lê os saldos antes, que
    é a ordem em que a decisão realmente acontece.
  */

  /**
   * A VAGA RECOMENDADA PARA A ENTRADA.
   *
   * Era o único passo que sobrou sem ajuda: escolhida a saída, a pessoa ainda
   * caçava uma vaga no desenho. O sistema já sabia tudo para responder —
   * ocupação, andar e módulo de cada vaga — e não dizia nada.
   *
   * O critério, nesta ordem:
   *
   *   1. VAGA LIVRE. Enfiar carga onde já há coisa é decisão de quem conhece o
   *      produto, não palpite de tela;
   *   2. MESMO ANDAR da saída. Descer um palete do andar 9 para o 1 é
   *      empilhadeira, fila e risco; andar igual é carrinho;
   *   3. MÓDULO MAIS PRÓXIMO. Entre duas iguais, a que anda menos.
   *
   * É SUGESTÃO, não trava: toda vaga continua clicável. E ela NÃO aparece
   * depois que o destino foi escolhido — sugestão cumprida vira ruído.
   *
   * O critério de proximidade é uma aposta minha, não uma regra que a operação
   * me deu. Se o certo for agrupar por família, ou encher o andar baixo
   * primeiro, muda esta função e nada mais.
   */
  const vagaSugerida = React.useMemo<number | null>(() => {
    if (origem === null || destino !== null || vagaDaOrigem === null) return null;

    const livres = [...vagasDaRua.values()].filter(
      (v) => v.saldo === 0 && v.localizador !== origem.localizador,
    );
    if (livres.length === 0) return null;

    const melhor = livres.reduce((m, v) => {
      const peso = (x: VagaMapa): number =>
        /* O andar pesa 1.000 módulos: nenhuma distância horizontal compensa
           trocar de nível, que é o que muda o equipamento. */
        Math.abs(x.nivel - vagaDaOrigem.nivel) * 1000 +
        Math.abs(x.moduloOrdem - vagaDaOrigem.moduloOrdem);
      return peso(v) < peso(m) ? v : m;
    }, livres[0]!);
    return melhor.localizador;
  }, [origem, destino, vagaDaOrigem, vagasDaRua]);

  const escolherProduto = (sku: number, descricao: string): void => {
    definirProduto(sku);
    definirDescricaoProduto(descricao);
    definirOrigem(null);
    definirVagaDaOrigem(null);
    definirDestino(null);
    definirRuaVista(null);
  };

  const pontaDaVaga = (v: VagaMapa, saldoSku: number | null): Ponta => ({
    localizador: v.localizador,
    endereco: comoEndereco(v.endereco),
    corredor: ruaVista ?? lerEndereco(v.endereco).corredor,
    saldoSku,
    pct: v.pct,
    saldoPosicao: v.saldo,
  });

  const sensores = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const arrastadoEhOrigem =
    arrastando !== null && saldoPorVaga.has(locDoId(arrastando) ?? -1);
  const modoArraste: ModoArraste = arrastando === null ? null : arrastadoEhOrigem ? 'carga' : null;

  const aoSoltar = (evento: DragEndEvent): void => {
    const ativo = String(evento.active.id);
    const solto = sobreRef.current;
    definirArrastando(null);

    const locOrigem = locDoId(ativo);
    if (locOrigem === null) return;
    const saldo = saldoPorVaga.get(locOrigem);
    if (saldo === undefined) return;

    const vagaOrigem = vagasNaTela.current.get(locOrigem);
    const daLista = comSaldo.find((p) => p.localizador === locOrigem);
    definirOrigem(
      vagaOrigem
        ? pontaDaVaga(vagaOrigem, saldo)
        : {
            localizador: locOrigem,
            endereco: comoEndereco(daLista?.descricao ?? ''),
            corredor: daLista?.corredor ?? '',
            saldoSku: saldo,
            pct: null,
            saldoPosicao: null,
          },
    );

    // Soltar fora de uma vaga não é gesto perdido: define só a origem, e o
    // operador continua procurando o destino — que é exatamente o caso de
    // quem sabe a rua mas precisa ver a ocupação antes de escolher a vaga.
    if (solto === null || solto === locOrigem) return;
    const vagaDestino = vagasNaTela.current.get(solto);
    if (vagaDestino) {
      definirDestino(pontaDaVaga(vagaDestino, saldoPorVaga.get(solto) ?? null));
      definirConfirmando(true);
    }
  };

  const cartaoArrastado =
    arrastando !== null
      ? (comSaldo.find((p) => p.localizador === locDoId(arrastando)) ?? null)
      : null;

  if (centro === null) {
    return (
      <EstadoVazio
        titulo="Escolha um centro de estoque"
        detalhe="A transferência acontece sempre dentro de um único centro."
      />
    );
  }

  return (
    <DndContext
      sensors={sensores}
      onDragStart={(e: DragStartEvent) => definirArrastando(String(e.active.id))}
      onDragEnd={aoSoltar}
      onDragCancel={() => definirArrastando(null)}
    >
      <div className="flex h-full min-h-0 flex-col gap-2 p-3 sm:p-4">
        <header className="flex flex-wrap items-center gap-2">
          <span className="flex shrink-0 items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primaria-suave text-primaria">
              <Shuffle className="h-[18px] w-[18px]" />
            </span>
            <span className="leading-tight">
              <span className="rotulo block">Movimentação</span>
              <h1
                className="text-[17px] font-bold leading-none tracking-[-0.02em]"
                title="Tire de uma posição e guarde em outra, arrastando. Comece pelo produto ou pela posição."
              >
                Transferências
              </h1>
            </span>
          </span>

          <BuscaDePartida centro={centro} aoEscolherProduto={escolherProduto} />

          <Pilula tom="neutro">
            <Lock className="h-3 w-3" />
            mesmo centro ({centro})
          </Pilula>

          {produto !== null ? (
            <Botao
              variante="secundaria"
              tamanho="pequeno"
              onClick={alternarPainel}
              title={
                painelAberto
                  ? 'Encolhe o topo numa linha e devolve a altura ao desenho'
                  : 'Mostra foto, EAN, peso e as duas pontas por extenso'
              }
            >
              {painelAberto ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {painelAberto ? 'Mais desenho' : 'Mais detalhes'}
            </Botao>
          ) : null}

          {/*
            O SELETOR ENTRA NA LINHA, e não flutuando por cima dela.

            Ele vinha absoluto no canto, para não empurrar o desenho da rua
            para baixo. Só que o cabeçalho cresceu — ganhou a pílula do centro
            e o botão de detalhes — e o que era um canto vazio virou o lugar
            onde eles ficam: a pílula "mesmo centro (3201)" desaparecia debaixo
            do seletor, ilegível.

            Na linha, ele não cobre nada. Em tela larga não custa altura
            nenhuma, e em tela estreita ele quebra para a linha de baixo — que
            é onde, de qualquer forma, já não havia espaço.
          */}
          <span className="ml-auto shrink-0">{seletor}</span>
        </header>

        {produto === null ? (
          <div className="cartao grid flex-1 place-items-center p-10">
            <EstadoVazio
              icone={<Package className="h-7 w-7" />}
              titulo="Escolha o que vai ser movido"
              detalhe="Digite o SKU, parte da descrição, ou o código/endereço de uma posição para ver o que há nela."
            />
          </div>
        ) : (
          <>
            {painelAberto ? (
              <FaixaDoMovimento
                sku={produto}
                descricao={descricaoProduto}
                resumo={resumoSku}
                origem={origem}
                destino={destino}
                aoLimparOrigem={() => {
                  definirOrigem(null);
                  definirVagaDaOrigem(null);
                }}
                aoLimparDestino={() => definirDestino(null)}
                aoConfirmar={() => definirConfirmando(true)}
              />
            ) : (
              <LinhaResumo
                sku={produto}
                origem={origem}
                destino={destino}
                aoConfirmar={() => definirConfirmando(true)}
                aoLimparOrigem={() => {
                  definirOrigem(null);
                  definirVagaDaOrigem(null);
                }}
                aoLimparDestino={() => definirDestino(null)}
              />
            )}

            <div className="cartao flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              {/*
                O MAPA É O MESMO NOS DOIS PASSOS. O que muda é a FAIXA de cima
                — quais ruas ela navega e o que o número de cada uma quer
                dizer. Na saída, as ruas que têm o SKU e quanto há em cada uma;
                no destino, todas as ruas do centro e quantas vagas estão
                livres. A decisão em si é sempre um clique na vaga do desenho.

                Foi o desenho que sumiu quando tentei resolver a confusão com
                uma lista — e é ele que mostra o CD de verdade. A confusão não
                era o mapa: era a rua ter virado um PASSO, competindo com a
                escolha da posição. Aqui ela volta a ser só navegação.
              */}
              {origem === null ? (
                <FaixaRuas
                  titulo="De onde sai · escolha a rua, depois a vaga"
                  ruas={ruasComSku.map((r) => ({
                    nome: r.nome,
                    valor: r.saldo,
                    sufixo: 'un',
                    detalhe: `${r.posicoes} ${r.posicoes === 1 ? 'posição' : 'posições'}`,
                  }))}
                  ruaVista={ruaVista}
                  aoEscolher={definirRuaVista}
                />
              ) : (
                /*
                  Sem barra de "trocar a saída" aqui: desfazer a saída é o X da
                  FAIXA DO MOVIMENTO, logo acima — ela já mostra as duas pontas
                  por extenso e cada uma com o seu X. Repetir a ação numa
                  segunda barra é o mesmo erro que a rua-como-passo criou: dois
                  controles para a mesma coisa.
                */
                <FaixaRuas
                    titulo="Para onde vai · escolha a rua, depois a vaga"
                    ruas={ruasDestino.map((r) => ({
                      nome: r.nome,
                      valor: r.livres,
                      sufixo: r.livres === 1 ? 'livre' : 'livres',
                      alerta: r.livres === 0,
                      marca: r.nome === origem.corredor ? 'saída' : undefined,
                    }))}
                  ruaVista={ruaVista}
                  aoEscolher={definirRuaVista}
                  comAtalho
                />
              )}

              <div className="min-h-0 flex-1 overflow-auto">
                <RackRua

                      centro={centro}
                      corredor={ruaVista}
                      comSku={saldoPorVaga}
                      origem={origem?.localizador ?? null}
                      destino={destino?.localizador ?? null}
                      alerta={null}
                      modoArraste={modoArraste}
                      sobreLoc={sobreLoc}
                      ruaAnterior={ruaAnterior}
                      ruaSeguinte={ruaSeguinte}
                      aoPaginarRua={paginarRua}
                      registrarVagas={registrarVagas}
                      focar={origem?.localizador ?? null}
                sugerida={vagaSugerida}
                nivelDaSaida={origem !== null && destino === null ? (vagaDaOrigem?.nivel ?? null) : null}
                      aoClicarVaga={(v, saldo) => {
                        // Clique faz o mesmo que o arraste: em quem tem saldo vira
                        // saída; em qualquer outra vira entrada.
                        if (saldo !== null && origem === null) {
                          definirOrigem(pontaDaVaga(v, saldo));
                          definirVagaDaOrigem(v);
                          return;
                        }
                        if (origem !== null && v.localizador !== origem.localizador) {
                          definirDestino(pontaDaVaga(v, saldo));
                          definirConfirmando(true);
                          return;
                        }
                        if (saldo !== null) {
                          definirOrigem(pontaDaVaga(v, saldo));
                          definirVagaDaOrigem(v);
                        }
                      }}
                      dica={
                        origem === null ? (
                          <>
                            <b className="text-texto">De onde sai</b>: clique numa vaga de{' '}
                            <b className="text-primaria">borda azul</b> — são as que têm este SKU.
                            Outra rua? Escolha na faixa acima.
                          </>
                        ) : vagaSugerida !== null ? (
                          /* Nomear a sugerida por extenso: o realce diz ONDE,
                             o texto diz POR QUÊ — e é o porquê que faz alguém
                             aceitar ou ignorar a sugestão com segurança. */
                          <>
                            <b className="text-texto">Sugerido</b>:{' '}
                            <b className="font-mono text-status-livre">
                              {comoEndereco(vagasDaRua.get(vagaSugerida)?.endereco ?? '')}
                            </b>{' '}
                            — vaga livre, mesmo andar da saída. Clique nela, ou escolha outra
                            qualquer.
                          </>
                        ) : (
                          <>
                            <b className="text-texto">Para onde vai</b>: arraste a carga até a
                            vaga, ou clique nela. Outra rua? Escolha na faixa acima, ou arraste até
                            a borda e segure — direita avança, esquerda volta.
                          </>
                        )
                      }
                    />
                  </div>
            </div>
          </>
        )}
      </div>

      {/* `pointerEvents: none` no WRAPPER, não só no cartão de dentro.
          O wrapper do dnd-kit fica exatamente sob o cursor, e sem isso ele
          respondia ao `elementFromPoint` no lugar da vaga: o arraste começava,
          o cartão seguia o mouse, e soltar não fazia nada. */}
      <DragOverlay
        dropAnimation={null}
        style={{ width: 'auto', height: 'auto', pointerEvents: 'none' }}
      >
        {cartaoArrastado ? (
          <div className="gradiente-marca pointer-events-none flex -translate-y-[118%] items-center gap-2 rounded-xl px-3 py-2 text-white shadow-forte">
            <Boxes className="h-5 w-5" />
            <span className="leading-tight">
              <span className="block text-[11px] font-semibold uppercase tracking-wide opacity-80">
                saindo de
              </span>
              <span className="block font-mono text-[12.5px] font-bold">
                {comoEndereco(cartaoArrastado.descricao)}
              </span>
              <span className="block text-[10.5px] opacity-80">
                {numero(cartaoArrastado.saldo)} un · solte na vaga de entrada
              </span>
            </span>
          </div>
        ) : null}
      </DragOverlay>

      {produto !== null && origem && destino ? (
        <DialogoConfirmacao
          aberto={confirmando}
          centro={centro}
          produto={produto}
          descricaoProduto={descricaoProduto}
          origem={origem}
          destino={destino}
          aoFechar={() => definirConfirmando(false)}
          aoConcluir={() => {
            definirConfirmando(false);
            definirOrigem(null);
            definirDestino(null);
            void posicoes.refetch();
          }}
        />
      ) : null}
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Entrada: por produto, por descrição ou por posição
// ---------------------------------------------------------------------------

function BuscaDePartida({
  centro,
  aoEscolherProduto,
}: {
  centro: number;
  aoEscolherProduto: (sku: number, descricao: string) => void;
}): JSX.Element {
  const [termo, definirTermo] = React.useState('');
  const [erro, definirErro] = React.useState<string | null>(null);
  const [produtos, definirProdutos] = React.useState<{ produto: number; descricao: string }[]>([]);
  const [posicao, definirPosicao] = React.useState<PosicaoResolvida | null>(null);

  const limpar = (): void => {
    definirErro(null);
    definirProdutos([]);
    definirPosicao(null);
  };

  /**
   * Uma caixa só, três respostas.
   *
   * Só dígitos → SKU. Com separador (`-`, `/`, espaço) ou não achando SKU →
   * posição, e aí a tela lista o que há dentro dela para o operador escolher
   * qual produto mover. Texto → descrição.
   */
  const buscar = useMutation({
    mutationFn: async (t: string) => {
      const soDigitos = /^\d+$/.test(t);
      const pareceEndereco = /[\s\-/]/.test(t);
      if (pareceEndereco || soDigitos) {
        try {
          const p = await api.get<PosicaoResolvida>('/api/localizador/resolver', { centro, q: t });
          if (p.produtos.length > 0 || !soDigitos) return { tipo: 'posicao' as const, posicao: p };
        } catch {
          // Não é posição — segue para a busca normal.
        }
      }
      const r = await api.get<RespostaBusca>('/api/busca', { centro, q: t });
      return { tipo: 'busca' as const, busca: r };
    },
    onSuccess: (r) => {
      limpar();
      if (r.tipo === 'posicao') {
        definirPosicao(r.posicao);
        if (r.posicao.produtos.length === 0) {
          definirErro(`${comoEndereco(r.posicao.endereco)} não tem saldo de nenhum SKU.`);
        }
        return;
      }
      const b = r.busca;
      if (b.tipoDetectado === 'sku') {
        aoEscolherProduto(Number(b.termo), resumoDoSku(b));
        definirTermo('');
        return;
      }
      if (b.tipoDetectado === 'descricao') {
        if (b.produtos.length === 0) {
          // Número que não virou SKU: o produto pode existir, só não tem saldo
          // em nenhuma posição deste centro (ex.: SKU da TCL no centro 1101).
          definirErro(
            /^\d+$/.test(b.termo.trim())
              ? `SKU ${b.termo.trim()} sem saldo no centro ${centro}.`
              : 'Nada encontrado para este termo.',
          );
        }
        definirProdutos(b.produtos);
        return;
      }
      definirErro('Endereço encontrado, mas sem produto. Digite o código do localizador.');
    },
    onError: (e) => {
      limpar();
      definirErro(e instanceof ErroHttp ? e.message : 'Não foi possível buscar.');
    },
  });

  /**
   * SUGESTÃO ENQUANTO SE DIGITA — o passo que faltava.
   *
   * A caixa só respondia depois do "Buscar", e quem digita um SKU quase certo
   * (um dígito trocado) descobria o erro uma volta de rede depois, com a tela
   * limpa e nada explicando. Digitando, as opções aparecem embaixo e o
   * trabalho vira um clique.
   *
   * Reaproveita `/api/busca`, a mesma do Ctrl+K: número vira o SKU com o total
   * que ele tem no centro, texto vira lista de produtos. O `Buscar` continua,
   * porque ele também resolve ENDEREÇO (`/api/localizador/resolver`), que a
   * sugestão não tenta adivinhar.
   */
  const termoAtrasado = useDebounce(termo, 250);
  const sugestoes = useQuery<RespostaBusca>({
    queryKey: ['transferencias', 'sugestao', centro, termoAtrasado],
    queryFn: () => api.get<RespostaBusca>('/api/busca', { centro, q: termoAtrasado }),
    /* Nada de sugerir enquanto a busca explícita está em voo, nem depois que
       ela já respondeu: seriam duas listas competindo na mesma gaveta. */
    enabled:
      centro !== null &&
      termoAtrasado.trim().length >= 2 &&
      !buscar.isPending &&
      produtos.length === 0 &&
      posicao === null,
    staleTime: 30_000,
  });

  const sugeridos = React.useMemo<ProdutoEncontrado[]>(() => {
    const d = sugestoes.data;
    if (!d) return [];
    if (d.tipoDetectado === 'sku') {
      return [
        {
          produto: Number(d.termo),
          descricao: resumoDoSku(d),
        },
      ];
    }
    /* Oito é o que cabe sem a gaveta virar uma tela: quem passa disso está
       digitando pouco demais, e o caminho é digitar mais. */
    return d.tipoDetectado === 'descricao' ? d.produtos.slice(0, 8) : [];
  }, [sugestoes.data]);

  /* A lista explícita ganha da sugestão: se a pessoa apertou Buscar, é aquilo
     que ela pediu que tem de aparecer. */
  const listaProdutos = produtos.length > 0 ? produtos : sugeridos;

  const temResultado = Boolean(erro) || listaProdutos.length > 0 || posicao !== null;

  return (
    <div className="relative min-w-[260px] flex-1">
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const t = termo.trim();
          if (t.length >= 2) buscar.mutate(t);
        }}
      >
        <span className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
          <Campo
            value={termo}
            onChange={(e) => definirTermo(e.target.value)}
            placeholder="SKU, descrição, ou código / endereço da posição"
            aria-label="O que você quer mover"
            className="min-h-[36px] pl-9 text-[13px]"
          />
        </span>
        <Botao
          type="submit"
          variante="primaria"
          tamanho="pequeno"
          carregando={buscar.isPending}
          disabled={termo.trim().length < 2}
        >
          Buscar
        </Botao>
      </form>

      {/* Resultado flutua sobre o conteúdo: escolher o produto é um momento,
          não um painel permanente, e painel permanente aqui rouba a altura do
          desenho, que é onde a decisão acontece. */}
      {temResultado ? (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 rounded-xl border border-borda bg-superficie p-2.5 shadow-forte">
          <button
            type="button"
            onClick={limpar}
            aria-label="Fechar resultados"
            className="absolute right-1.5 top-1.5 rounded-md p-1 text-textoSuave hover:bg-superficie2 hover:text-texto"
          >
            <X className="h-3.5 w-3.5" />
          </button>

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-1.5 text-[12px] text-textoFraco">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
          {erro}
        </p>
      ) : null}

      {listaProdutos.length > 0 ? (
        <div>
          <p className="rotulo mb-1.5">
            {produtos.length > 0
              ? `${produtos.length} produtos — escolha qual mover`
              : `${listaProdutos.length === 1 ? 'Encontrado' : `${listaProdutos.length} encontrados`} — clique para mover`}
          </p>
          <div className="flex max-h-[120px] flex-wrap gap-1.5 overflow-y-auto">
            {listaProdutos.map((p) => (
              <button
                key={p.produto}
                type="button"
                onClick={() => {
                  aoEscolherProduto(p.produto, p.descricao);
                  limpar();
                  definirTermo('');
                }}
                className="flex max-w-[340px] items-center gap-2 rounded-lg border border-borda bg-superficie px-2.5 py-1.5 text-left shadow-xs hover:border-primaria/40 hover:bg-primaria-suave"
              >
                <ImagemProduto sku={p.produto} tamanho={26} />
                <span className="min-w-0 leading-tight">
                  <span className="block font-mono text-[11.5px] font-bold">{p.produto}</span>
                  <span className="block truncate text-[11px] text-textoFraco">{p.descricao}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {posicao ? (
        <div className="mt-1.5">
          <p className="rotulo mb-1.5 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            {comoEndereco(posicao.endereco)} · LOC {posicao.localizador} —{' '}
            {posicao.produtos.length} {posicao.produtos.length === 1 ? 'produto' : 'produtos'}.
            Escolha qual mover
          </p>
          <div className="flex max-h-[120px] flex-wrap gap-1.5 overflow-y-auto">
            {posicao.produtos.map((p) => (
              <button
                key={p.sku}
                type="button"
                onClick={() => {
                  aoEscolherProduto(p.sku, p.descricao);
                  limpar();
                  definirTermo('');
                }}
                className="flex max-w-[340px] items-center gap-2 rounded-lg border border-borda bg-superficie px-2.5 py-1.5 text-left shadow-xs hover:border-primaria/40 hover:bg-primaria-suave"
              >
                <ImagemProduto sku={p.sku} tamanho={26} />
                <span className="min-w-0 leading-tight">
                  <span className="block font-mono text-[11.5px] font-bold">{p.sku}</span>
                  <span className="block truncate text-[11px] text-textoFraco">{p.descricao}</span>
                </span>
                <span
                  className={cn(
                    'tabular rounded-md px-1.5 py-0.5 text-[11.5px] font-bold',
                    p.quantidade > 0
                      ? 'bg-superficie2'
                      : 'bg-status-divergenciaFundo text-status-divergencia',
                  )}
                  // Saldo <= 0 aqui nao impede escolher o SKU: so quer dizer
                  // que a saida tera de sair de outra vaga, e a tira de baixo
                  // mostra quais.
                  title={
                    p.quantidade > 0
                      ? `${numero(p.quantidade)} un nesta vaga`
                      : 'sem saldo nesta vaga — a saída terá de sair de outra'
                  }
                >
                  {numero(p.quantidade)}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** O painel recolhido: uma linha, ainda dizendo o essencial. */
function LinhaResumo({
  sku,
  origem,
  destino,
  aoConfirmar,
  aoLimparOrigem,
  aoLimparDestino,
}: {
  sku: number;
  origem: Ponta | null;
  destino: Ponta | null;
  aoConfirmar: () => void;
  aoLimparOrigem: () => void;
  aoLimparDestino: () => void;
}): JSX.Element {
  return (
    <div className="cartao flex flex-wrap items-center gap-x-2.5 gap-y-1 px-2.5 py-1.5 text-[12px]">
      <ImagemProduto sku={sku} tamanho={22} arredondada={false} className="rounded" />
      <span className="font-mono text-[12px] font-bold">{sku}</span>
      <span className="rotulo">sai de</span>
      {/* O X TAMBÉM NA LINHA ENCOLHIDA.

          Ele só existia no painel aberto, e quem trabalha encolhido — que é o
          caso de quem quer altura para o desenho — ficava sem como desfazer a
          ponta sem antes expandir o topo. Desfazer não pode custar dois
          cliques a mais que fazer. */}
      <PontaEncolhida
        endereco={origem?.endereco ?? null}
        classe="text-status-livre"
        sufixo={origem ? `${numero(origem.saldoSku ?? 0)} un` : null}
        aoLimpar={origem ? aoLimparOrigem : null}
      />
      <ArrowRight className="h-3.5 w-3.5 text-textoSuave" />
      <span className="rotulo">entra em</span>
      <PontaEncolhida
        endereco={destino?.endereco ?? null}
        classe="text-primaria"
        sufixo={null}
        aoLimpar={destino ? aoLimparDestino : null}
      />
      <Botao
        variante="primaria"
        tamanho="micro"
        className="ml-auto"
        disabled={!origem || !destino}
        onClick={aoConfirmar}
      >
        Conferir e transferir
      </Botao>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A faixa do movimento: produto, saída, entrada
// ---------------------------------------------------------------------------

/**
 * O que está errado neste SKU, na própria tela: os endereços negativos e o
 * saldo sem endereço. É a diferença entre o total do PBS e o que está em
 * posição — aberta ao clicar no selo "a corrigir", para quem vê o número não
 * ter de ir procurar onde ele mora.
 *
 * Negativo se acerta transferindo para ele, a partir de um endereço que tenha
 * o SKU (aqui mesmo, ou pela tela de Negativos, que sugere a origem). O saldo
 * sem endereço (localizador 0) só se acerta no PBS.
 */
function ErrosDoSku({ resumo, aoFechar }: { resumo: NumerosDoSku; aoFechar: () => void }): JSX.Element {
  return (
    <div className="order-last basis-full rounded-lg border border-status-atencao/40 bg-status-atencaoFundo/40 px-3 py-2 text-[12px]">
      <div className="flex items-center gap-2">
        <p className="font-semibold text-status-atencao">
          {numero(Math.abs(resumo.aCorrigir))} un a corrigir — já descontadas do total, como no PBS
        </p>
        <button type="button" onClick={aoFechar} className="ml-auto text-textoFraco hover:text-texto" aria-label="Fechar">
          ✕
        </button>
      </div>
      {resumo.negativos.length > 0 ? (
        <div className="mt-1.5">
          <p className="text-textoFraco">
            {resumo.negativos.length === 1 ? 'Endereço com saldo negativo' : 'Endereços com saldo negativo'} — saiu
            mais do que tinha. Conte e transfira para ele:
          </p>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {resumo.negativos.map((n) => (
              <li
                key={n.localizador}
                className="tabular rounded-md border border-borda bg-superficie px-2 py-0.5"
                title={`Localizador ${n.localizador}`}
              >
                <b>{comoEndereco(n.descricao)}</b>{' '}
                <span className="font-semibold text-status-divergencia">{numero(n.saldo)} un</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {resumo.semEndereco !== 0 ? (
        <p className="mt-1.5 text-textoFraco">
          <b className="tabular text-status-divergencia">{numero(resumo.semEndereco)} un</b> lançadas sem endereço
          (localizador 0) — só se acerta no PBS.
        </p>
      ) : null}
      <p className="mt-1.5">
        <Link to="/movimentos/negativos" className={acaoTexto}>
          Abrir a tela de Negativos
        </Link>{' '}
        <span className="text-textoFraco">— ela sugere de onde tirar para zerar.</span>
      </p>
    </div>
  );
}

function FaixaDoMovimento({
  sku,
  descricao,
  resumo,
  origem,
  destino,
  aoLimparOrigem,
  aoLimparDestino,
  aoConfirmar,
}: {
  sku: number;
  descricao: string;
  resumo: NumerosDoSku | null;
  origem: Ponta | null;
  destino: Ponta | null;
  aoLimparOrigem: () => void;
  aoLimparDestino: () => void;
  aoConfirmar: () => void;
}): JSX.Element {
  const ficha = useProduto(sku);
  const p = ficha.data;
  const [verErros, definirVerErros] = React.useState(false);
  React.useEffect(() => definirVerErros(false), [sku]);

  return (
    <div className="cartao flex flex-wrap items-stretch gap-2 p-2">
      {verErros && resumo && resumo.aCorrigir !== 0 ? (
        <ErrosDoSku resumo={resumo} aoFechar={() => definirVerErros(false)} />
      ) : null}
      <div className="flex min-w-[240px] flex-1 items-center gap-2">
        <ImagemProduto sku={sku} tamanho={36} arredondada={false} className="rounded-lg" />
        <div className="min-w-0">
          <p className="truncate text-[12.5px] font-bold leading-tight">{p?.nome ?? descricao}</p>
          <p className="flex flex-wrap items-center gap-x-2 text-[10.5px] text-textoFraco">
            <span>
              SKU <b className="font-mono text-texto">{sku}</b>
            </span>
            {/* Sem nome na ficha, o título já é este resumo: não repete. */}
            {resumo && p?.nome ? (
              <span className="tabular" title="Saldo do SKU no centro, igual ao PBS.">
                <b className="text-texto">{numero(resumo.total)} un</b> em {numero(resumo.posicoes)}{' '}
                {resumo.posicoes === 1 ? 'posição' : 'posições'}
              </span>
            ) : null}
            {resumo && resumo.aCorrigir !== 0 ? (
              <button
                type="button"
                onClick={() => definirVerErros((v) => !v)}
                aria-expanded={verErros}
                className="tabular rounded bg-status-atencaoFundo px-1.5 font-semibold text-status-atencao hover:underline"
                title="Ver onde está o erro"
              >
                {numero(Math.abs(resumo.aCorrigir))} un a corrigir {verErros ? '▴' : '▾'}
              </button>
            ) : null}
            {p?.ean ? (
              <span>
                EAN <b className="font-mono text-texto">{p.ean}</b>
              </span>
            ) : null}
            {p?.pesoBrutoKg != null ? (
              <span className="tabular">
                {p.pesoBrutoKg.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} kg/un
              </span>
            ) : null}
            {p?.volumeM3 != null ? (
              <span className="tabular">
                {p.volumeM3.toLocaleString('pt-BR', { minimumFractionDigits: 3 })} m³/un
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <PontaDoMovimento
        rotulo="Sai de"
        tom="origem"
        ponta={origem}
        vazio="clique ou arraste uma vaga de borda azul"
        aoLimpar={aoLimparOrigem}
      />
      <PontaDoMovimento
        rotulo="Entra em"
        tom="destino"
        ponta={destino}
        vazio="solte em qualquer vaga do desenho"
        aoLimpar={aoLimparDestino}
      />

      <Botao
        variante="primaria"
        tamanho="pequeno"
        className="self-center"
        disabled={!origem || !destino}
        onClick={aoConfirmar}
      >
        <ArrowRight className="h-4 w-4" />
        Conferir e transferir
      </Botao>
    </div>
  );
}

function PontaDoMovimento({
  rotulo,
  tom,
  ponta,
  vazio,
  aoLimpar,
}: {
  rotulo: string;
  tom: 'origem' | 'destino';
  ponta: Ponta | null;
  vazio: string;
  aoLimpar: () => void;
}): JSX.Element {
  const geo = ponta ? lerEndereco(ponta.endereco) : null;
  const alcance = comoAlcancar(geo?.andar ?? null);
  return (
    <div
      className={cn(
        'flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border px-2.5 py-1.5',
        !ponta && 'border-dashed border-bordaForte bg-superficie2',
        ponta && tom === 'origem' && 'border-status-livre/40 bg-status-livreFundo',
        ponta && tom === 'destino' && 'border-primaria/40 bg-primaria-suave',
      )}
    >
      <div className="min-w-0 flex-1 leading-tight">
        <span className="rotulo block">{rotulo}</span>
        {ponta ? (
          <>
            <span className="block font-mono text-[13.5px] font-bold leading-none">
              {ponta.endereco}
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[10.5px] text-textoFraco">
              <span title={DICA_ALCANCE} className="inline-flex items-center gap-1">
                <alcance.Icone className="h-3 w-3" />
                {alcance.texto}
              </span>
              <span aria-hidden>·</span>
              {tom === 'origem' ? (
                <>
                  tem <b className="tabular text-texto">{numero(ponta.saldoSku ?? 0)}</b> un deste
                  SKU
                </>
              ) : ponta.saldoPosicao === 0 ? (
                <b className="text-status-livre">vaga livre</b>
              ) : ponta.pct !== null ? (
                <>
                  vaga <b className="tabular text-texto">{ponta.pct}%</b> ocupada
                </>
              ) : (
                'ocupação não medida'
              )}
            </span>
          </>
        ) : (
          <span className="text-[11.5px] italic text-textoSuave">{vazio}</span>
        )}
      </div>
      {ponta ? (
        <Botao variante="fantasma" tamanho="micro" aria-label={`Limpar ${rotulo}`} onClick={aoLimpar}>
          <X className="h-3.5 w-3.5" />
        </Botao>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tira das posições que têm o SKU
// ---------------------------------------------------------------------------

function DialogoConfirmacao({
  aberto,
  centro,
  produto,
  descricaoProduto,
  origem,
  destino,
  aoFechar,
  aoConcluir,
}: {
  aberto: boolean;
  centro: number;
  produto: number;
  descricaoProduto: string;
  origem: Ponta;
  destino: Ponta;
  aoFechar: () => void;
  aoConcluir: () => void;
}): JSX.Element {
  const [quantidade, definirQuantidade] = React.useState('');

  React.useEffect(() => {
    if (aberto) definirQuantidade(String(Math.max(1, origem.saldoSku ?? 1)));
  }, [aberto, origem]);

  const qtd = Number(quantidade);
  const qtdValida = Number.isInteger(qtd) && qtd > 0;
  const ficha = useProduto(produto);

  const simulacao = useQuery<Simulacao>({
    queryKey: ['simular', centro, produto, origem.localizador, destino.localizador, qtd],
    queryFn: () =>
      api.post<Simulacao>(
        '/api/movimentacao/simular',
        { produto, origem: origem.localizador, destino: destino.localizador, quantidade: qtd },
        { centro },
      ),
    enabled: aberto && qtdValida,
  });

  const mover = useMutation({
    mutationFn: () =>
      api.post('/api/movimentacao', {
        centro,
        produto,
        quantidade: qtd,
        origem: origem.localizador,
        destino: destino.localizador,
      }),
    onSuccess: aoConcluir,
  });

  const s = simulacao.data;
  const maximo = origem.saldoSku ?? 0;

  return (
    <Dialog.Root open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/55 backdrop-blur-[3px] data-[state=open]:animate-aparecer" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-borda bg-fundo shadow-forte data-[state=open]:animate-escalar">
          <header className="flex items-start gap-3 border-b border-borda bg-superficie px-4 py-3">
            <ImagemProduto sku={produto} tamanho={44} arredondada={false} className="rounded-xl" />
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[15px] font-bold tracking-[-0.01em]">
                Confirmar transferência
              </Dialog.Title>
              <Dialog.Description className="truncate text-[12.5px] text-textoFraco">
                <span className="font-mono font-semibold">{produto}</span> ·{' '}
                {ficha.data?.nome ?? descricaoProduto}
                {ficha.data?.ean ? ` · EAN ${ficha.data.ean}` : ''}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Botao variante="fantasma" tamanho="icone" aria-label="Fechar">
                <X className="h-5 w-5" />
              </Botao>
            </Dialog.Close>
          </header>

          <div className="space-y-3 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="leading-tight">
                <span className="rotulo block">Sai de</span>
                <span className="font-mono text-[14px] font-bold text-status-livre">
                  {origem.endereco}
                </span>
                <span className="tabular block text-[11px] text-textoFraco">
                  tem {numero(origem.saldoSku ?? 0)} un
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-textoSuave" />
              <span className="leading-tight">
                <span className="rotulo block">Entra em</span>
                <span className="font-mono text-[14px] font-bold text-primaria">
                  {destino.endereco}
                </span>
                <span className="tabular block text-[11px] text-textoFraco">
                  {destino.saldoPosicao === 0
                    ? 'vaga livre'
                    : destino.pct !== null
                      ? `${destino.pct}% ocupada`
                      : 'ocupação não medida'}
                </span>
              </span>
            </div>

            <label className="block">
              <span className="rotulo mb-1 block">Quantas unidades transferir</span>
              <span className="flex items-center gap-2">
                <Campo
                  value={quantidade}
                  onChange={(e) => definirQuantidade(e.target.value.replace(/\D/g, ''))}
                  inputMode="numeric"
                  autoFocus
                  className="tabular w-[130px] text-[18px] font-bold"
                  aria-label="Quantidade a transferir"
                />
                <Botao
                  variante="suave"
                  tamanho="pequeno"
                  onClick={() => definirQuantidade(String(maximo))}
                >
                  tudo ({numero(maximo)})
                </Botao>
                {qtd > maximo ? (
                  <span className="text-[11.5px] font-semibold text-status-divergencia">
                    acima do saldo da origem
                  </span>
                ) : null}
              </span>
            </label>

            {simulacao.isLoading ? (
              <p className="flex items-center gap-2 text-[12px] text-textoFraco">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                conferindo saldo e espaço…
              </p>
            ) : null}

            {s?.ok ? (
              <p className="tabular rounded-lg bg-superficie2 px-3 py-2 text-[12px] text-textoFraco">
                saldo deste SKU no destino:{' '}
                <b className="text-texto">{numero(s.saldoDestinoAntes)}</b> →{' '}
                <b className="text-texto">{numero(s.saldoDestinoDepois)}</b>
                {s.pctDestinoDepois !== null ? (
                  <> · vaga passa a {s.pctDestinoDepois}% ocupada</>
                ) : null}
              </p>
            ) : null}

            {s?.impedimentos.map((m) => (
              <p
                key={m}
                className="flex items-start gap-2 rounded-lg border border-status-divergencia/30 bg-status-divergenciaFundo px-3 py-1.5 text-[12px] text-status-divergencia"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {m}
              </p>
            ))}
            {s?.avisos.map((m) => (
              <p
                key={m}
                className="flex items-start gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-1.5 text-[12px] text-textoFraco"
              >
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
                {m}
              </p>
            ))}
            {mover.isError ? <EstadoErro erro={mover.error} className="py-2" /> : null}

            <p className="text-[11px] text-textoFraco">
              Ao confirmar, o Kardex recebe duas linhas: <b>saída</b> em {origem.endereco} e{' '}
              <b>entrada</b> em {destino.endereco}, mesmo SKU e mesma quantidade, no centro{' '}
              {centro}.
            </p>
          </div>

          <footer className="flex items-center justify-end gap-2 border-t border-borda bg-superficie px-4 py-3">
            <Botao variante="fantasma" onClick={aoFechar}>
              <Undo2 className="h-4 w-4" />
              Voltar e ajustar
            </Botao>
            <Botao
              variante="primaria"
              disabled={!qtdValida || !s?.ok}
              carregando={mover.isPending}
              onClick={() => mover.mutate()}
            >
              {!mover.isPending ? <Check className="h-4 w-4" /> : null}
              Transferir {qtdValida ? numero(qtd) : ''} un
            </Botao>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// As duas transferências
// ---------------------------------------------------------------------------

/**
 * Transferências — dois gestos diferentes sob o mesmo menu.
 *
 * **Entre posições** move a mercadoria de prateleira dentro do MESMO centro:
 * é imediato, uma procedure do PBS valida e grava, e o kardex recebe as duas
 * linhas na hora.
 *
 * **Entre centros** move de um centro para outro — VENDAS para RMA, por
 * exemplo. Aí o ERP exige aprovação: criar gera só uma pendência, e é a
 * aprovação que move o estoque.
 *
 * Duas abas e não dois itens de menu porque, para quem opera, a pergunta é a
 * mesma ("preciso tirar isto daqui e pôr ali") e o que muda é a distância. Com
 * itens separados, escolher o menu já exigiria saber a regra do ERP.
 *
 * **Posições em lote** é o primeiro gesto com muitos itens de uma vez: o
 * formulário TRANSFERECIA_LOCALIZADORES do Procfit — bipar, colar ou importar
 * a lista, conferir, gravar. Mesmo centro, sem aprovação.
 */
export function Transferencias(): JSX.Element {
  const { empresa, centro, centroAtual } = useContexto();
  const [abaUrl, definirAbaUrl] = useParamUrl('aba');
  const aba = abaUrl === 'centros' || abaUrl === 'lote' ? abaUrl : 'posicoes';

  const seletor = (
    <Segmentado
      tamanho="pequeno"
      valor={aba}
      aoMudar={(v) => definirAbaUrl(v === 'posicoes' ? null : v)}
      rotuloAcessivel="Tipo de transferência"
      opcoes={[
        { valor: 'posicoes', rotulo: 'Entre posições' },
        { valor: 'lote', rotulo: 'Posições em lote' },
        { valor: 'centros', rotulo: 'Entre centros' },
      ]}
    />
  );

  if (aba === 'posicoes') {
    /* O seletor desce como prop e é montado DENTRO do cabeçalho do painel —
       ver o comentário lá. Flutuando, ele cobria a pílula do centro. */
    return <TransferenciasEntrePosicoes seletor={seletor} />;
  }

  return (
    <div className="space-y-4 p-3 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="leading-tight">
          <span className="rotulo block">Movimentação</span>
          <h1 className="text-[17px] font-bold leading-none tracking-[-0.02em]">
            {aba === 'lote'
              ? 'Transferência entre localizadores em lote'
              : 'Transferência entre centros'}
          </h1>
        </span>
        {/* Em lote, tudo acontece DENTRO do centro do cabeçalho — e gravar
            move estoque na hora. Dizer qual é, em letra grande, evita gravar
            no centro errado por ter esquecido o seletor lá em cima. */}
        {aba === 'lote' && centro !== null ? (
          <span className="rounded-lg border border-primaria/30 bg-primaria-suave px-2.5 py-1 text-[13px] font-semibold text-primaria">
            Centro <span className="tabular font-mono">{centro}</span>
            {centroAtual?.nome ? ` · ${centroAtual.nome}` : ''}
          </span>
        ) : null}
        <div className="ml-auto">{seletor}</div>
      </div>
      {aba === 'lote' ? (
        <TransferenciaLocalizadores
          centro={centro}
          centroNome={centroAtual?.nome ?? null}
          controlaLocalizador={centroAtual?.controlaLocalizador ?? true}
        />
      ) : (
        <TransferenciaCentros empresa={empresa} centro={centro} />
      )}
    </div>
  );
}

/**
 * A faixa de ruas que navega o desenho — a MESMA nos dois passos.
 *
 * Ela não escolhe origem nem destino: escolhe qual rua está no mapa. Quem
 * decide é o clique na vaga, sempre. Foi essa separação que faltou quando a
 * rua virou um passo e passou a competir com a escolha da posição.
 *
 * O que muda entre os passos é o CONJUNTO e o NÚMERO:
 *
 *   - na saída  — só as ruas que têm o SKU, e quanto há em cada uma;
 *   - no destino — todas as ruas do centro, e quantas vagas estão livres.
 *
 * Em ambos o número é o que decide para onde olhar primeiro. Nome de rua
 * sozinho não decide nada.
 */
function FaixaRuas({
  titulo,
  ruas,
  ruaVista,
  aoEscolher,
  comAtalho = false,
}: {
  titulo: string;
  ruas: readonly {
    nome: string;
    valor: number;
    sufixo: string;
    detalhe?: string | undefined;
    alerta?: boolean | undefined;
    marca?: string | undefined;
  }[];
  ruaVista: string | null;
  aoEscolher: (rua: string) => void;
  /** O `select` de salto. Só no destino, onde o conjunto é o CD inteiro. */
  comAtalho?: boolean;
}): JSX.Element | null {
  const faixa = React.useRef<HTMLDivElement>(null);

  /* Traz a rua aberta para a vista: com dezenas de ruas, a ativa costuma
     nascer fora da faixa e a pessoa não vê onde está. */
  React.useEffect(() => {
    faixa.current?.querySelector('[data-ativa="1"]')?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
    });
  }, [ruaVista]);

  if (ruas.length === 0) return null;

  return (
    <div className="border-b border-borda bg-superficie2 px-2.5 py-1.5">
      <div className="mb-1 flex items-center gap-2">
        <p className="rotulo">{titulo}</p>
        {comAtalho ? (
          /*
            O SELECT É ATALHO, não uma segunda decisão. A faixa responde "onde
            tem espaço", que é a pergunta de quem não sabe o destino; o select
            responde "me leva na rua 47", que é a de quem já sabe. Com 77 ruas,
            achar uma pelo nome rolando a faixa é trabalho.
          */
          <select
            value={ruaVista ?? ''}
            onChange={(e) => aoEscolher(e.target.value)}
            aria-label="Ir para uma rua"
            title="Atalho: pular direto para uma rua pelo nome"
            className="ml-auto min-h-[26px] shrink-0 rounded-lg border border-borda bg-superficie px-1.5 text-[11.5px] font-semibold shadow-xs"
          >
            <option value="" disabled>
              ir para a rua…
            </option>
            {ruas.map((r) => (
              <option key={r.nome} value={r.nome}>
                {r.nome} · {r.valor} {r.sufixo}
              </option>
            ))}
          </select>
        ) : (
          <span className="tabular ml-auto text-[11px] text-textoFraco">
            {numero(ruas.length)} {ruas.length === 1 ? 'rua' : 'ruas'}
          </span>
        )}
      </div>

      <div ref={faixa} className="flex gap-1.5 overflow-x-auto pb-0.5">
        {ruas.map((r) => {
          const ativa = r.nome === ruaVista;
          return (
            <button
              key={r.nome}
              type="button"
              data-ativa={ativa ? '1' : '0'}
              onClick={() => aoEscolher(r.nome)}
              aria-pressed={ativa}
              title={`Rua ${r.nome} · ${r.valor} ${r.sufixo}${r.detalhe ? ` · ${r.detalhe}` : ''}`}
              className={cn(
                'shrink-0 rounded-lg border px-2 py-1 text-left leading-tight transition-colors',
                ativa
                  ? 'border-primaria bg-primaria-suave'
                  : 'border-borda bg-superficie hover:border-bordaForte hover:bg-superficie2',
              )}
            >
              <span className="flex items-center gap-1">
                <span
                  className={cn(
                    'font-mono text-[12px] font-bold',
                    ativa ? 'text-primaria' : 'text-texto',
                  )}
                >
                  {r.nome}
                </span>
                {/* A rua da saída é o destino mais provável — e também o único
                    em que a carga não anda. Marcar evita os dois enganos. */}
                {r.marca ? (
                  <span className="text-[9px] font-bold uppercase tracking-wide text-textoSuave">
                    {r.marca}
                  </span>
                ) : null}
              </span>
              <span className="tabular block text-[10.5px] text-textoFraco">
                <b className={r.alerta ? 'text-status-atencao' : 'text-texto'}>{numero(r.valor)}</b>{' '}
                {r.sufixo}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Uma ponta na linha encolhida: endereço, quantidade e o X para desfazer. */
function PontaEncolhida({
  endereco,
  classe,
  sufixo,
  aoLimpar,
}: {
  endereco: string | null;
  classe: string;
  sufixo: string | null;
  aoLimpar: (() => void) | null;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('font-mono font-bold', classe)}>{endereco ?? '—'}</span>
      {sufixo ? (
        <span className="tabular text-[10.5px] text-textoFraco">({sufixo})</span>
      ) : null}
      {aoLimpar ? (
        <button
          type="button"
          onClick={aoLimpar}
          aria-label={`Desfazer ${endereco ?? ''}`}
          title="Desfazer esta ponta"
          className="rounded p-0.5 text-textoSuave transition-colors hover:bg-superficie2 hover:text-status-divergencia"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}
